import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { nombreProyectoDesdeLista } from '../../../utils/projectsFromApi';
import {
  formatearRangoFechas,
  formatearFechaHora,
  etiquetaSemana,
  formatearPeriodoTitulo,
  getMatrizResuelta,
} from '../../../data/mockCiclosCiclicos';
import { fetchCiclico, updateCiclico, deleteCiclico, fetchDatosPlaneamientoCiclicos } from '../../../services/ciclicoService';
import { eliminarSalidasPorCicloId } from '../../../data/mockSalidasProduccion';
import Toast from '../../../components/Common/Toast';
import ConfirmModal from '../../../components/Common/ConfirmModal';
import { exportarCiclicoMenuExcel } from '../../../utils/ciclicoMenuExcel';

const DIAS_LABEL = [
  { key: 'lunes', label: 'LUNES' },
  { key: 'martes', label: 'MARTES' },
  { key: 'miercoles', label: 'MIERCOLES' },
  { key: 'jueves', label: 'JUEVES' },
  { key: 'viernes', label: 'VIERNES' },
  { key: 'sabado', label: 'SABADO' },
  { key: 'domingo', label: 'DOMINGO' },
];

const DATALIST_PLATOS_ID = 'platos-menu-ciclo-global';

function claseCelda(diaActivo) {
  if (!diaActivo) return 'bg-gray-100 text-gray-500';
  return 'bg-white text-gray-900';
}

/** Convierte el texto guardado en líneas (varios platos unidos con " + "). Vacío = []. */
function parseLineasCelda(valor) {
  if (valor == null || String(valor).trim() === '') return [];
  const s = String(valor).trim();
  /* Placeholder antiguo: se muestra como celda sin menú */
  if (s === 'XX') return [];
  const parts = s
    .split(/\s*\+\s*/)
    .map((x) => x.trim())
    .filter((x) => x.length > 0);
  return parts.length ? parts : [];
}

function joinLineasCelda(lineas) {
  return lineas.map((x) => x.trim()).filter(Boolean).join(' + ');
}

/** Vista lectura: vacío = no se cocinó / no hay plato (nada visible) */
function TextoCeldaLectura({ texto }) {
  const raw = texto ?? '';
  const t = raw.trim();
  if (!t || t === 'XX') {
    return (
      <span className="inline-block min-h-[1em] w-full" aria-label="Sin menú este día">
        <span className="sr-only">Sin menú este día</span>
      </span>
    );
  }
  const partes = parseLineasCelda(raw);
  if (partes.length === 0) {
    return (
      <span className="inline-block min-h-[1em] w-full" aria-label="Sin menú este día">
        <span className="sr-only">Sin menú este día</span>
      </span>
    );
  }
  return (
    <span className="leading-snug">
      {partes.map((p, i) => (
        <React.Fragment key={`${i}-${p.slice(0, 20)}`}>
          {i > 0 ? <span className="text-gray-500"> + </span> : null}
          {p}
        </React.Fragment>
      ))}
    </span>
  );
}

/** Solo buscador (input + datalist) + añadir / quitar líneas. Sin &lt;select&gt;. */
function CeldaEditable({ value, onChange }) {
  const [lineas, setLineas] = useState(() => parseLineasCelda(value));

  useEffect(() => {
    setLineas(parseLineasCelda(value));
  }, [value]);

  const emitir = useCallback(
    (next) => {
      setLineas(next);
      onChange(joinLineasCelda(next));
    },
    [onChange],
  );

  const cambiarLinea = (i, v) => {
    const n = [...lineas];
    n[i] = v;
    emitir(n);
  };

  const quitarLinea = (i) => {
    if (lineas.length <= 1) {
      emitir([]);
      return;
    }
    emitir(lineas.filter((_, j) => j !== i));
  };

  const anadirLinea = () => {
    emitir([...lineas, '']);
  };

  return (
    <div className="flex flex-col gap-1.5 min-w-0">
      {lineas.map((linea, i) => (
        <div key={i} className="flex gap-0.5 items-center min-w-0">
          <label className="sr-only">Buscar plato {i + 1}</label>
          <input
            type="text"
            list={DATALIST_PLATOS_ID}
            className="min-w-0 flex-1 rounded border border-gray-400 bg-white px-1.5 py-1 text-[10px] shadow-sm placeholder:text-gray-400 focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500"
            value={linea}
            onChange={(e) => cambiarLinea(i, e.target.value)}
            placeholder="Buscar plato…"
            autoComplete="off"
          />
          <button
            type="button"
            onClick={() => quitarLinea(i)}
            className="shrink-0 rounded border border-red-200 bg-red-50 px-1.5 py-0.5 text-xs font-bold text-red-700 hover:bg-red-100"
            title="Quitar línea"
            aria-label="Quitar línea"
          >
            −
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={anadirLinea}
        className="w-full rounded border border-teal-300 bg-teal-50 py-0.5 text-[10px] font-medium text-teal-800 hover:bg-teal-100"
      >
        + Añadir
      </button>
    </div>
  );
}

const CiclicoDetalle = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [ciclo, setCiclo] = useState(null);
  const [cargandoCiclo, setCargandoCiclo] = useState(true);
  const [editando, setEditando] = useState(false);
  const [draftMatriz, setDraftMatriz] = useState({});
  const [tiempos, setTiempos] = useState([]);
  const [tiposPlato, setTiposPlato] = useState([]);
  const [proyectos, setProyectos] = useState([]);
  const [plantillas, setPlantillas] = useState([]);
  /** Recetas elaboradas (nombre para autocompletar celdas), desde la misma API que proyectos/plantillas. */
  const [recetasCatalogo, setRecetasCatalogo] = useState([]);
  const [modalEliminarAbierto, setModalEliminarAbierto] = useState(false);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'success' });

  useEffect(() => {
    setEditando(false);
  }, [id]);

  useEffect(() => {
    let ok = true;
    (async () => {
      setCargandoCiclo(true);
      setCiclo(null);
      try {
        const c = await fetchCiclico(id);
        if (ok) setCiclo(c);
      } catch {
        if (ok) setCiclo(null);
      } finally {
        if (ok) setCargandoCiclo(false);
      }
    })();
    return () => {
      ok = false;
    };
  }, [id]);

  useEffect(() => {
    let ok = true;
    (async () => {
      try {
        const bundle = await fetchDatosPlaneamientoCiclicos();
        if (ok) {
          setProyectos(bundle.proyectos);
          setPlantillas(bundle.plantillas);
          setTiempos((bundle.tiempos || []).filter((x) => x.active));
          setTiposPlato((bundle.tiposPlato || []).filter((x) => x.active));
          setRecetasCatalogo(Array.isArray(bundle.recetas) ? bundle.recetas : []);
        }
      } catch {
        if (ok) {
          setProyectos([]);
          setPlantillas([]);
          setTiempos([]);
          setTiposPlato([]);
          setRecetasCatalogo([]);
        }
      }
    })();
    return () => {
      ok = false;
    };
  }, []);

  const plantilla = useMemo(
    () => plantillas.find((p) => String(p.id) === String(ciclo?.plantillaId)) || null,
    [plantillas, ciclo],
  );

  const nombreTiempo = (tiempoId) => tiempos.find((t) => String(t.id) === String(tiempoId))?.label || tiempoId;
  const nombreTipo = (tipoId) =>
    (tiposPlato.find((t) => String(t.id) === String(tipoId))?.name || tipoId).toUpperCase();

  const proyectoNombre = (proyectoId) => nombreProyectoDesdeLista(proyectoId, proyectos);

  const { matriz: matrizBase, grupos } = useMemo(
    () => (ciclo && plantilla ? getMatrizResuelta(ciclo, plantilla) : { matriz: {}, grupos: [] }),
    [ciclo, plantilla],
  );

  const matriz = editando ? draftMatriz : matrizBase;

  const dias = plantilla?.diasSemana;

  const iniciarEdicion = useCallback(() => {
    if (!ciclo || !plantilla) return;
    const { matriz: m } = getMatrizResuelta(ciclo, plantilla);
    setDraftMatriz(JSON.parse(JSON.stringify(m)));
    setEditando(true);
  }, [ciclo, plantilla]);

  const cancelarEdicion = useCallback(() => {
    setEditando(false);
  }, []);

  const guardar = useCallback(async () => {
    if (!ciclo?.id) return;
    try {
      const updated = await updateCiclico(ciclo.id, {
        matrizPlatos: JSON.parse(JSON.stringify(draftMatriz)),
      });
      setCiclo(updated);
      setEditando(false);
      setToast({ isOpen: true, message: 'Cambios guardados.', type: 'success' });
    } catch (e) {
      setToast({ isOpen: true, message: e.message || 'No se pudo guardar.', type: 'error' });
    }
  }, [ciclo, draftMatriz]);

  const confirmarEliminarCiclo = useCallback(async () => {
    if (!ciclo?.id) return;
    try {
      await deleteCiclico(ciclo.id);
      eliminarSalidasPorCicloId(ciclo.id);
      setToast({ isOpen: true, message: 'Cíclico eliminado correctamente.', type: 'success' });
      setTimeout(() => {
        navigate('/produccion-concesionaria/planeamiento/ciclicos', { replace: true });
      }, 900);
    } catch (e) {
      setToast({ isOpen: true, message: e.message || 'No se pudo eliminar el cíclico.', type: 'error' });
    }
  }, [ciclo, navigate]);

  const exportarExcel = useCallback(async () => {
    if (!ciclo || !plantilla || !grupos.length) {
      setToast({ isOpen: true, message: 'No hay grilla para exportar.', type: 'warning' });
      return;
    }
    try {
      await exportarCiclicoMenuExcel({
        grupoFilename: `${ciclo.id}-${ciclo.nombre}`,
        lineaTitulo: `CÍCLICO — PROGRAMACIÓN DE MENÚ — ${nombreProyectoDesdeLista(ciclo.proyectoId, proyectos).toUpperCase()}`,
        periodoTitulo: formatearPeriodoTitulo(ciclo.fechaInicio, ciclo.fechaFin),
        nombreCiclo: ciclo.nombre,
        nombrePlantilla: plantilla.nombre,
        etiquetaSemana: etiquetaSemana(ciclo.anio, ciclo.numeroSemana),
        periodoRango:
          ciclo.fechaInicio && ciclo.fechaFin ? formatearRangoFechas(ciclo.fechaInicio, ciclo.fechaFin) : '—',
        fechaConfigurado: formatearFechaHora(ciclo.fechaCreacion),
        diasSemana: dias,
        grupos,
        matriz,
        nombreTiempo,
        nombreTipo,
      });
      setToast({ isOpen: true, message: 'Excel descargado.', type: 'success' });
    } catch (e) {
      setToast({ isOpen: true, message: e.message || 'No se pudo exportar a Excel.', type: 'error' });
    }
  }, [ciclo, plantilla, grupos, matriz, dias, proyectos, nombreTiempo, nombreTipo]);

  const setCelda = useCallback((diaKey, rowKey, valor) => {
    setDraftMatriz((prev) => ({
      ...prev,
      [diaKey]: {
        ...(prev[diaKey] || {}),
        [rowKey]: valor,
      },
    }));
  }, []);

  if (cargandoCiclo) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <p className="text-gray-600 text-sm">Cargando cíclico…</p>
      </div>
    );
  }

  if (!ciclo) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <p className="text-gray-700">No se encontró el cíclico.</p>
        <Link
          to="/produccion-concesionaria/planeamiento/ciclicos"
          className="mt-4 inline-block text-teal-700 text-sm font-medium hover:underline"
        >
          ← Volver a Cíclicos
        </Link>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-[100%] mx-auto">
      <nav className="text-sm text-gray-600 mb-4">
        <Link to="/dashboard" className="hover:text-teal-600">
          Dashboard
        </Link>
        <span className="mx-2">/</span>
        <Link to="/produccion-concesionaria" className="hover:text-teal-600">
          Producción concesionaria
        </Link>
        <span className="mx-2">/</span>
        <Link to="/produccion-concesionaria/planeamiento/ciclicos" className="hover:text-teal-600">
          Cíclicos
        </Link>
        <span className="mx-2">/</span>
        <span className="text-gray-800">Detalle</span>
      </nav>

      <div className="rounded-xl border border-gray-300 bg-white shadow-md overflow-hidden">
        <div className="border-b-2 border-gray-800 bg-gray-50 px-4 py-4 md:px-6">
          <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
            <div className="flex-1 min-w-0" />
            <div className="flex flex-wrap gap-2 shrink-0">
              {plantilla && grupos.length > 0 && (
                <>
                  <button
                    type="button"
                    onClick={exportarExcel}
                    className="rounded-md border border-emerald-600 bg-white px-4 py-2 text-sm font-medium text-emerald-800 shadow-sm hover:bg-emerald-50 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-1"
                  >
                    Exportar Excel
                  </button>
                  {!editando ? (
                    <button
                      type="button"
                      onClick={iniciarEdicion}
                      className="rounded-md bg-teal-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-1"
                    >
                      Editar
                    </button>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={guardar}
                        className="rounded-md bg-teal-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-1"
                      >
                        Guardar
                      </button>
                      <button
                        type="button"
                        onClick={cancelarEdicion}
                        className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-1"
                      >
                        Cancelar
                      </button>
                    </>
                  )}
                </>
              )}
              {!editando && (
                <button
                  type="button"
                  onClick={() => setModalEliminarAbierto(true)}
                  className="rounded-md border border-red-300 bg-white px-4 py-2 text-sm font-medium text-red-700 shadow-sm hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-offset-1"
                >
                  Eliminar
                </button>
              )}
            </div>
          </div>

          <p className="text-[11px] md:text-xs font-bold tracking-wide text-gray-800 uppercase text-center leading-snug">
            CÍCLICO — PROGRAMACIÓN DE MENÚ — {proyectoNombre(ciclo.proyectoId).toUpperCase()}
          </p>
          <p className="mt-2 text-center text-sm md:text-base font-semibold text-gray-900">
            {formatearPeriodoTitulo(ciclo.fechaInicio, ciclo.fechaFin)}
          </p>
          <p className="mt-1 text-center text-xs text-gray-600">
            {ciclo.nombre}
            {plantilla && (
              <>
                {' '}
                · Plantilla: <span className="font-medium text-gray-800">{plantilla.nombre}</span>
              </>
            )}
          </p>
          <dl className="mt-3 flex flex-wrap justify-center gap-x-6 gap-y-1 text-xs text-gray-600">
            <div>
              <span className="text-gray-500">Semana: </span>
              {etiquetaSemana(ciclo.anio, ciclo.numeroSemana)}
            </div>
            <div>
              <span className="text-gray-500">Periodo: </span>
              {ciclo.fechaInicio && ciclo.fechaFin ? formatearRangoFechas(ciclo.fechaInicio, ciclo.fechaFin) : '—'}
            </div>
            <div>
              <span className="text-gray-500">Configurado: </span>
              {formatearFechaHora(ciclo.fechaCreacion)}
            </div>
          </dl>
          {editando && (
            <p className="mt-3 text-center text-xs text-teal-800 bg-teal-50 border border-teal-100 rounded-md px-3 py-2">
              Modo edición: <strong>busque</strong> el plato (sugerencias del catálogo) y use <strong>+ Añadir</strong> /{' '}
              <strong>−</strong> para varias opciones en la misma celda. Sábado y domingo no aplican en esta plantilla y
              permanecen bloqueados.
            </p>
          )}
        </div>

        {!plantilla ? (
          <div className="p-6 text-amber-800 text-sm bg-amber-50 border-t border-amber-100">
            No se encontró la plantilla <code className="text-xs">{ciclo.plantillaId}</code>.
          </div>
        ) : grupos.length === 0 ? (
          <div className="p-6 text-gray-600 text-sm">La plantilla no tiene tiempos configurados.</div>
        ) : (
          <div className="overflow-x-auto">
            {editando && (
              <datalist id={DATALIST_PLATOS_ID}>
                {recetasCatalogo.map((r) => (
                  <option key={r.id} value={r.name} />
                ))}
              </datalist>
            )}
            <table className="w-full min-w-[900px] border-collapse text-[11px] md:text-xs">
              <thead>
                <tr className="bg-gray-800 text-white">
                  <th
                    colSpan={2}
                    className="border border-gray-700 px-2 py-2 text-left font-semibold uppercase tracking-tight"
                  >
                    Tiempos / Tipo
                  </th>
                  {DIAS_LABEL.map(({ key, label }) => (
                    <th key={key} className="border border-gray-700 px-1 py-2 text-center font-bold min-w-[7rem]">
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {grupos.map((grupo) =>
                  grupo.filas.map((fila, idx) => (
                    <tr key={fila.rowKey} className="hover:bg-gray-50/80">
                      {idx === 0 && (
                        <td
                          rowSpan={grupo.filas.length}
                          className="border border-gray-300 bg-indigo-100 align-middle text-center font-bold text-[10px] md:text-[11px] uppercase text-indigo-950 w-10 md:w-12 px-1"
                          style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}
                        >
                          {nombreTiempo(grupo.tiempoId)}
                        </td>
                      )}
                      <td className="border border-gray-300 bg-gray-50 px-2 py-1.5 font-semibold text-gray-800 whitespace-nowrap">
                        {nombreTipo(fila.tipoPlatoId)}
                      </td>
                      {DIAS_LABEL.map(({ key }, colIdx) => {
                        const activo = dias?.[key];
                        const texto = matriz[key]?.[fila.rowKey] ?? '';
                        const base = claseCelda(activo);
                        const editable = editando && activo;
                        return (
                          <td
                            key={`${fila.rowKey}-${key}`}
                            className={`border border-gray-300 align-top ${editable ? 'p-1' : 'px-1.5 py-1.5'} ${base}`}
                          >
                            {editable ? (
                              <CeldaEditable value={texto} onChange={(v) => setCelda(key, fila.rowKey, v)} />
                            ) : activo ? (
                              <TextoCeldaLectura texto={texto} />
                            ) : (
                              '—'
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  )),
                )}
              </tbody>
            </table>
          </div>
        )}

        {plantilla && dias && (
          <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 text-xs text-gray-600">
            <span className="font-medium text-gray-700">Días aplicables en plantilla: </span>
            {DIAS_LABEL.filter(({ key }) => dias[key])
              .map(({ label }) => label)
              .join(', ') || '—'}
            <span className="text-gray-500 ml-2">(en gris: día no incluido en la plantilla)</span>
          </div>
        )}
      </div>

      <div className="mt-6 flex flex-wrap gap-4">
        <Link
          to="/produccion-concesionaria/planeamiento/ciclicos"
          className="text-teal-700 text-sm font-medium hover:underline"
        >
          ← Volver a Cíclicos
        </Link>
        <Link
          to="/produccion-concesionaria/planeamiento/plantillas-ciclicos"
          className="text-gray-600 text-sm hover:underline"
        >
          Plantillas cíclicos
        </Link>
      </div>

      <ConfirmModal
        isOpen={modalEliminarAbierto}
        onClose={() => setModalEliminarAbierto(false)}
        onConfirm={confirmarEliminarCiclo}
        title="Eliminar cíclico"
        message={`¿Eliminar el cíclico «${ciclo.nombre}»? Se borrarán también las salidas a producción guardadas para este ciclo en este equipo. Esta acción no se puede deshacer.`}
        confirmText="Eliminar"
        cancelText="Cancelar"
      />
      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, isOpen: false }))}
      />
    </div>
  );
};

export default CiclicoDetalle;
